/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

 function onUrlChange(callback) {
    let lastUrl = location.href;

    // Cria um método para verificar mudanças na URL
    const checkUrlChange = () => {
        const currentUrl = location.href;
        if (currentUrl !== lastUrl) {
            lastUrl = currentUrl;
            callback(currentUrl);
        }
    };

    // Substitui os métodos pushState e replaceState para capturar mudanças de URL
    const pushState = history.pushState;
    history.pushState = function() {
        pushState.apply(history, arguments);
        checkUrlChange();
    };

    const replaceState = history.replaceState;
    history.replaceState = function() {
        replaceState.apply(history, arguments);
        checkUrlChange();
    };

    // Adiciona um event listener para o evento popstate
    window.addEventListener('popstate', checkUrlChange);
    // Adiciona um event listener para o evento hashchange
    window.addEventListener('hashchange', checkUrlChange);
}



const loadCustomInstallment = () => {
    const installmentScripts = document.querySelectorAll('[data-script-content]');
    if(installmentScripts.length > 0){
        installmentScripts.forEach((item, idx) => {
            const functionName = item.getAttribute('data-script-content');
            const hasScript = document.querySelector([`[data-custom-function="${functionName}"]`]);
            if(!hasScript){
                const scriptContent = item.textContent;
                const newScript = document.createElement('script');
                newScript.setAttribute('data-custom-function', functionName);
                newScript.textContent = scriptContent;
                document.body.appendChild(newScript);
            }
        })
    }
}

const collectionCheckFilters = () => {
    const body = document.querySelector('body');
    if(body.classList.contains('template-collection') || body.classList.contains('template-search')){        
        onUrlChange((newUrl) => {
            setTimeout(() => {
                loadCustomInstallment();
            }, 1500)        
        });
    }
}

const checkCart = () => {
    const checkoutButton = document.querySelector(".checkout-buttons a");
  
    if (checkoutButton) {
      checkoutButton.addEventListener("click", function (event) {
        event.preventDefault(); // Impede o redirecionamento padrão
  
        fetch("/cart.json")
          .then(response => response.json())
          .then(cart => {
            if (cart.total_price <= 0) {
              alert("Você não atingiu o valor mínimo de compra.");
            } else {
              window.location.href = checkoutButton.href; // Redireciona para o checkout
            }
          })
          .catch(error => {
            console.error("Erro ao consultar o carrinho:", error);
          });
      });
    }
};

const brindeAutomaticMetafields = () => {
  const form = document.querySelector('.js-product-form');
  const button = form?.querySelector('.add-to-cart');
  if (!form || !button) return;

  button.addEventListener('click', async (e) => {
    e.preventDefault();

    const formData = new FormData(form);
    const mainVariantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || '1', 10);
    const brindeId = button.dataset.brindeId;
    const productTitle = button.dataset.product;

    const mainProduct = { id: mainVariantId, quantity };
    const items = [mainProduct];

    if (brindeId) {
      // Verificar se já existe um item com o mesmo variant_id sem properties no carrinho
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        // Encontrar itens com o mesmo variant_id que não têm properties.Brinde
        const existingItemsWithoutProperties = cart.items.filter(item => 
          item.variant_id == brindeId && 
          (!item.properties || !item.properties.Brinde)
        );
        
        // Remover itens existentes sem properties antes de adicionar o novo com properties
        for (const existingItem of existingItemsWithoutProperties) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: existingItem.key, quantity: 0 })
          });
        }
      } catch (error) {
        console.error('Erro ao verificar/remover itens existentes:', error);
      }

      items.push({
        id: brindeId,
        quantity: 1,
        properties: {
          Brinde: `${productTitle}`
        }
      });
    }

    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });

    // Verificar se o item correto foi adicionado com as properties
    // E garantir que apenas o item com properties permaneça no carrinho
    if (brindeId) {
      setTimeout(async () => {
        try {
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          
          // Encontrar o item que acabou de ser adicionado (com properties.Brinde)
          const giftItemWithProperties = cart.items.find(item => 
            item.variant_id == brindeId && 
            item.properties && 
            item.properties.Brinde === productTitle
          );
          
          // Encontrar TODOS os itens com o mesmo variant_id que não têm properties
          // (independente de terem desconto ou não)
          const giftItemsWithoutProperties = cart.items.filter(item => 
            item.variant_id == brindeId && 
            (!item.properties || !item.properties.Brinde)
          );
          
          // Se encontramos itens sem properties e temos um item com properties,
          // remover todos os itens sem properties para garantir que apenas o item correto permaneça
          if (giftItemsWithoutProperties.length > 0 && giftItemWithProperties) {
            // Remover todos os itens sem properties
            for (const wrongItem of giftItemsWithoutProperties) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: wrongItem.key, quantity: 0 })
              });
            }
            
            // Aguardar um pouco e verificar novamente se o desconto foi aplicado ao item correto
            setTimeout(async () => {
              const updatedCartResponse = await fetch('/cart.js');
              const updatedCart = await updatedCartResponse.json();
              
              const updatedGiftItem = updatedCart.items.find(item => 
                item.variant_id == brindeId && 
                item.properties && 
                item.properties.Brinde === productTitle
              );
              
              // Se o item com properties ainda não tem desconto, mas deveria ter,
              // o desconto será aplicado automaticamente pelo sistema de desconto do Shopify
              // quando o carrinho for atualizado
              
              // Disparar evento para atualizar o carrinho e aplicar descontos
              document.dispatchEvent(new CustomEvent('on:cart:change', {
                bubbles: true,
                cancelable: false
              }));
            }, 300);
          }
        } catch (error) {
          console.error('Erro ao verificar propriedades do brinde:', error);
        }
      }, 800);
    }

    const notificationTemplate = document.getElementById('AddedNotification');
    if (notificationTemplate) {
        const notification = notificationTemplate.content.cloneNode(true);
        const addedNotification = notification.querySelector('added-notification');
        addedNotification.dataset.productTitle = productTitle;
        document.body.appendChild(addedNotification);
    }

    document.dispatchEvent(new CustomEvent('on:cart:change', {
      bubbles: true,
      cancelable: false
    }));

    setTimeout(() => {
      brindeAutomaticMetafieldsCart();
    }, 800);
  });
};

  
  const brindeAutomaticMetafieldsCart = () => {
    //cart remove
    document.querySelectorAll('.quantity .quantity-down[data-gift-metafields="true"]').forEach(button => {
        button.addEventListener('click', function(event) {
            const container = this.closest('.quantity');
            const input = container.querySelector('input[type="number"]');
            const currentValue = parseInt(input.value, 10);
            setTimeout(() => {
                if (currentValue === 1) {
                    event.preventDefault();
                    
                    // Pegar o título do produto pai que está sendo removido
                    const cartItem = this.closest('.cart-item');
                    const productTitle = cartItem.querySelector('.cart-item__title a')?.textContent?.trim();
                    const giftVariantId = this.getAttribute('data-gift-id');
           
                    // Buscar o carrinho e encontrar o brinde com properties.Brinde igual ao título do produto pai
                    fetch('/cart.js')
                        .then(response => response.json())
                        .then(cart => {
                            // Encontrar o brinde que tem properties.Brinde igual ao título do produto pai
                            const giftItem = cart.items.find(item => 
                                item.variant_id == giftVariantId && 
                                item.properties && 
                                item.properties.Brinde === productTitle
                            );

                            if (giftItem) {
                                // Remover o brinde usando a key do item (não o variant_id)
                                fetch('/cart/change.js', {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                        id: giftItem.key,
                                        quantity: 0
                                    }),
                                })
                                .then(response => response.json())
                                .then(data => {
                                    console.log('Brinde removido do carrinho', data);

                                    // Atualizar o carrinho
                                    setTimeout(() => {
                                        document.dispatchEvent(new CustomEvent('on:cart:change', {
                                            bubbles: true,
                                            cancelable: false
                                        }));
                                    }, 800)  
                                })
                                .catch(error => {
                                    console.error('Erro ao remover o brinde do carrinho:', error);
                                });
                            }
                        })
                        .catch(error => {
                            console.error('Erro ao buscar o carrinho:', error);
                        });
                }
            }, 800) 
        });
    });
};

// Função para remover brinde quando produto pai é removido diretamente (botão remove)
const removeBrindeWhenParentRemoved = () => {
    document.addEventListener('on:cart:change', async () => {
        // Aguardar um pouco para o carrinho ser atualizado
        setTimeout(async () => {
            try {
                const cart = await fetch('/cart.js').then(res => res.json());
                
                // Buscar todos os brindes no carrinho que têm properties.Brinde
                const giftItems = cart.items.filter(item => 
                    item.properties && 
                    item.properties.Brinde
                );
                
                // Para cada brinde, verificar se o produto pai ainda está no carrinho
                giftItems.forEach(giftItem => {
                    const parentProductTitle = giftItem.properties.Brinde;
                    
                    // Verificar se o produto pai ainda existe no carrinho
                    const parentProductExists = cart.items.some(item => 
                        item.product_title === parentProductTitle && 
                        (!item.properties || !item.properties.Brinde) // Não é um brinde
                    );
                    
                    // Se o produto pai não existe mais, remover o brinde
                    if (!parentProductExists) {
                        fetch('/cart/change.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                id: giftItem.key,
                                quantity: 0
                            })
                        })
                        .then(response => response.json())
                        .then(() => {
                            console.log(`Brinde removido automaticamente após remoção do produto pai: ${parentProductTitle}`);
                            // Não disparar outro evento on:cart:change aqui para evitar loop infinito
                            // O carrinho será atualizado naturalmente
                        })
                        .catch(error => {
                            console.error('Erro ao remover brinde:', error);
                        });
                    }
                });
            } catch (error) {
                console.error('Erro ao verificar remoção de brinde:', error);
            }
        }, 1000);
    });
};


document.addEventListener('DOMContentLoaded', function() {
    loadCustomInstallment();
    collectionCheckFilters();
    checkCart();
    brindeAutomaticMetafields();
    brindeAutomaticMetafieldsCart();
    removeBrindeWhenParentRemoved();
});

window.addEventListener('on:quickbuy:after-open', (event) => {

    loadCustomInstallment();

    setTimeout(() => {
        brindeAutomaticMetafields();
        brindeAutomaticMetafieldsCart();
    }, 1800)  
});